import React, { useState, useEffect } from 'react';
import {
  FolderKanban,
  ArrowLeft,
  Building2,
  Calendar,
  Receipt,
  Layers,
  Award,
  Hammer,
  Boxes,
  ShoppingCart,
  Calculator,
  FileText,
  DollarSign,
  TrendingUp,
  FolderArchive,
  History,
  CheckCircle2,
  AlertCircle,
  Plus,
  MessageSquare,
} from 'lucide-react';
import { WhatsAppShareModal, WhatsAppShareData } from '../components/common/WhatsAppShareModal.tsx';

interface ProjectDetailPageProps {
  projectId: string;
  onBack: () => void;
  onNavigate: (route: string, param?: string) => void;
}

export const ProjectDetailPage: React.FC<ProjectDetailPageProps> = ({
  projectId,
  onBack,
  onNavigate,
}) => {
  const [project, setProject] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string>('Overview');

  // WhatsApp Sharing State
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
  const [shareData, setShareData] = useState<WhatsAppShareData | null>(null);

  const fetchProjectData = async () => {
    try {
      setLoading(true);
      const res = await fetch(`/api/projects/${projectId}`);
      if (res.ok) {
        const data = await res.json();
        setProject(data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjectData();
  }, [projectId]);

  const formatPKR = (val: string | number) => {
    return 'Rs. ' + Math.round(Number(val || 0)).toLocaleString('en-PK');
  };

  if (loading) {
    return <div className="p-8 text-center text-sm text-slate-500">Loading project details...</div>;
  }

  if (!project) {
    return (
      <div className="p-8 text-center space-y-4">
        <div className="text-slate-500 text-sm">Project not found.</div>
        <button onClick={onBack} className="text-xs font-semibold text-blue-700 underline">
          Back to Projects
        </button>
      </div>
    );
  }

  const tabs = [
    'Overview',
    'BOQ',
    'Quotation',
    'Purchase',
    'Inventory',
    'Site Work',
    'Progress',
    'Cost',
    'Invoices',
    'Payments',
    'Documents',
    'Activity',
  ];

  return (
    <div className="space-y-6">
      {/* Top Navigation & Project Badge */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-2 rounded-lg bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
            title="Back to list"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-bold text-lg sm:text-xl text-slate-900">
                {project.projectNumber}
              </span>
              <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-blue-100 text-blue-800">
                {project.status.replace(/_/g, ' ')}
              </span>
            </div>
            <h1 className="text-sm font-semibold text-slate-700">{project.projectName}</h1>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => {
              setShareData({
                title: `Project Dossier - ${project.projectNumber}`,
                docType: 'DOCUMENT',
                referenceNumber: project.projectNumber,
                projectName: project.projectName,
                branchName: 'UBL Branch Premises',
                amount: formatPKR(project.contractValue),
                date: project.startDate,
                detailsSummary: `Status: ${project.status}. PO #: ${project.poNumber || 'PO on file'}. PM: ${project.projectManager}. Site Eng: ${project.siteEngineer}. Actual Cost: ${formatPKR(project.actualCost)}`,
              });
              setWhatsAppModalOpen(true);
            }}
            className="flex items-center gap-1.5 px-3 py-2 bg-[#25D366]/20 hover:bg-[#25D366]/30 text-[#075e54] border border-[#25D366]/40 rounded-lg text-xs font-bold transition-colors"
            title="Share project brief via WhatsApp"
          >
            <MessageSquare className="w-4 h-4 text-[#25D366]" />
            <span>WhatsApp Dossier</span>
          </button>

          <div className="bg-white px-3 py-1.5 rounded-lg border border-slate-200 text-right">
            <div className="text-[10px] text-slate-400 font-semibold uppercase">Contract Value</div>
            <div className="text-sm font-bold text-blue-900">{formatPKR(project.contractValue)}</div>
          </div>
          <div className="bg-white px-3 py-1.5 rounded-lg border border-slate-200 text-right">
            <div className="text-[10px] text-slate-400 font-semibold uppercase">Gross Profit</div>
            <div className="text-sm font-bold text-emerald-700">{formatPKR(project.profit)}</div>
          </div>
        </div>
      </div>

      {/* Tabs navigation */}
      <div className="flex items-center gap-1 overflow-x-auto border-b border-slate-200 bg-white p-1 rounded-xl shadow-2xs">
        {tabs.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
              activeTab === tab
                ? 'bg-[#0f2942] text-white shadow-2xs'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Tab Content 1: Overview */}
      {activeTab === 'Overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Quick Metrics */}
            <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-2xs space-y-4">
              <h2 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                Key Parameters &amp; PO Information
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-xs">
                <div>
                  <div className="text-slate-400 font-medium">UBL Branch</div>
                  <div className="font-bold text-slate-800 mt-0.5">
                    {project.branch?.branchName} ({project.branch?.branchCode})
                  </div>
                  <div className="text-[11px] text-slate-500">{project.branch?.city}</div>
                </div>
                <div>
                  <div className="text-slate-400 font-medium">Client PO Number</div>
                  <div className="font-bold text-slate-800 mt-0.5">
                    {project.poNumber || 'PO Pending'}
                  </div>
                  <div className="text-[11px] text-slate-500">Date: {project.poDate || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-slate-400 font-medium">Execution Timeline</div>
                  <div className="font-bold text-slate-800 mt-0.5">{project.startDate}</div>
                  <div className="text-[11px] text-slate-500">Target: {project.targetCompletionDate}</div>
                </div>
                <div>
                  <div className="text-slate-400 font-medium">Project Manager</div>
                  <div className="font-bold text-slate-800 mt-0.5">{project.projectManager}</div>
                </div>
                <div>
                  <div className="text-slate-400 font-medium">Site Engineer</div>
                  <div className="font-bold text-slate-800 mt-0.5">{project.siteEngineer}</div>
                </div>
                <div>
                  <div className="text-slate-400 font-medium">UBL Field Rep</div>
                  <div className="font-bold text-slate-800 mt-0.5">
                    {project.branch?.ublEngineer || 'UBL Area Engineer'}
                  </div>
                </div>
              </div>
            </div>

            {/* Linked Ticket Preview */}
            {project.ticket && (
              <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-2xs space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                    Linked Maintenance Request
                  </span>
                  <span className="text-xs font-bold text-blue-900">{project.ticket.ticketNumber}</span>
                </div>
                <div className="text-xs font-bold text-slate-800">{project.ticket.subject}</div>
                <p className="text-xs text-slate-600">{project.ticket.description}</p>
              </div>
            )}
          </div>

          {/* Financial Summary Card */}
          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-2xs space-y-4">
            <h2 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
              Project Financial Health
            </h2>

            <div className="space-y-3 text-xs">
              <div className="flex items-center justify-between py-1.5 border-b border-slate-100">
                <span className="text-slate-500">Contract / PO Value</span>
                <span className="font-bold text-slate-900">{formatPKR(project.contractValue)}</span>
              </div>
              <div className="flex items-center justify-between py-1.5 border-b border-slate-100">
                <span className="text-slate-500">Estimated Budget</span>
                <span className="font-bold text-slate-700">{formatPKR(project.budget)}</span>
              </div>
              <div className="flex items-center justify-between py-1.5 border-b border-slate-100">
                <span className="text-slate-500">Actual Cost Incurred</span>
                <span className="font-bold text-slate-700">{formatPKR(project.actualCost)}</span>
              </div>
              <div className="flex items-center justify-between py-2 bg-emerald-50 px-2 rounded-lg text-emerald-900 font-bold">
                <span>Net Estimated Margin</span>
                <span>{formatPKR(project.profit)}</span>
              </div>
            </div>

            <div className="pt-2">
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-slate-500">Budget Spent</span>
                <span className="font-semibold text-slate-800">
                  {Math.round((Number(project.actualCost) / (Number(project.contractValue) || 1)) * 100)}%
                </span>
              </div>
              <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                <div
                  className="bg-blue-600 h-full rounded-full"
                  style={{
                    width: `${Math.min(
                      100,
                      (Number(project.actualCost) / (Number(project.contractValue) || 1)) * 100
                    )}%`,
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab Content 2: BOQ */}
      {activeTab === 'BOQ' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-2xs p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-bold text-slate-900">Project Bill of Quantities (BOQ)</h2>
              <p className="text-xs text-slate-500">Detailed civil &amp; finishing items with revision history</p>
            </div>
            <button
              onClick={() => onNavigate('boqs')}
              className="text-xs font-semibold text-blue-700 hover:text-blue-900 bg-blue-50 px-3 py-1.5 rounded-lg"
            >
              Open Full BOQ Module →
            </button>
          </div>

          {Array.isArray(project.boqs) && project.boqs.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {project.boqs.map((b: any) => (
                <div key={b.id} className="py-3 flex items-center justify-between">
                  <div>
                    <div className="font-bold text-xs text-slate-900">
                      {b.boqNumber} - Revision {b.revisionNumber}
                    </div>
                    <div className="text-xs text-slate-600">{b.title}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-sm text-slate-900">{formatPKR(b.grandTotal)}</div>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
                      {b.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-6 text-center text-xs text-slate-400">No BOQ generated yet for this project.</div>
          )}
        </div>
      )}

      {/* Tab Content 3: Quotation */}
      {activeTab === 'Quotation' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-2xs p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold text-slate-900">Official Quotations</h2>
            <button
              onClick={() => onNavigate('quotations')}
              className="text-xs font-semibold text-blue-700 hover:text-blue-900 bg-blue-50 px-3 py-1.5 rounded-lg"
            >
              Go to Quotations →
            </button>
          </div>
          {Array.isArray(project.quotations) && project.quotations.length > 0 ? (
            <div className="space-y-3">
              {project.quotations.map((q: any) => (
                <div key={q.id} className="p-3 bg-slate-50 rounded-lg border border-slate-200 flex items-center justify-between">
                  <div>
                    <div className="font-bold text-xs text-blue-900">{q.quotationNumber}</div>
                    <div className="text-xs text-slate-500">Date: {q.date} • Validity: {q.validityDays} Days</div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-sm text-slate-900">{formatPKR(q.grandTotal)}</div>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">
                      {q.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-6 text-center text-xs text-slate-400">No quotation generated yet.</div>
          )}
        </div>
      )}

      {/* Tab Content 4: Purchase */}
      {activeTab === 'Purchase' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-2xs p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold text-slate-900">Procurement &amp; Purchase Orders</h2>
            <button
              onClick={() => onNavigate('purchase')}
              className="text-xs font-semibold text-blue-700 hover:text-blue-900 bg-blue-50 px-3 py-1.5 rounded-lg"
            >
              Manage Orders →
            </button>
          </div>
          {Array.isArray(project.purchaseOrders) && project.purchaseOrders.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {project.purchaseOrders.map((po: any) => (
                <div key={po.id} className="py-3 flex items-center justify-between text-xs">
                  <div>
                    <div className="font-bold text-blue-900">{po.poNumber}</div>
                    <div className="text-slate-500">Date: {po.poDate}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-slate-900">{formatPKR(po.grandTotal)}</div>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-slate-100 text-slate-700">
                      {po.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-6 text-center text-xs text-slate-400">No direct purchase orders registered.</div>
          )}
        </div>
      )}

      {/* Tab Content 5: Inventory */}
      {activeTab === 'Inventory' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-2xs p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-bold text-slate-900">Materials Issued to Project</h2>
              <p className="text-xs text-slate-500">Cement bags, floor tiles, paint drums and hardware</p>
            </div>
            <button
              onClick={() => onNavigate('inventory')}
              className="text-xs font-semibold text-blue-700 hover:text-blue-900 bg-blue-50 px-3 py-1.5 rounded-lg"
            >
              Issue More Materials →
            </button>
          </div>
          {Array.isArray(project.materialIssues) && project.materialIssues.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {project.materialIssues.map((m: any) => (
                <div key={m.id} className="py-2.5 flex items-center justify-between text-xs">
                  <div>
                    <div className="font-semibold text-slate-800">{m.remarks || 'Store Issue'}</div>
                    <div className="text-[11px] text-slate-400">Ref: {m.referenceNo} • Qty: {m.quantity}</div>
                  </div>
                  <div className="font-bold text-slate-900">{formatPKR(m.totalCost)}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-6 text-center text-xs text-slate-400">No materials issued to this project yet.</div>
          )}
        </div>
      )}

      {/* Tab Content 6: Site Work & Progress */}
      {(activeTab === 'Site Work' || activeTab === 'Progress') && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-2xs p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-bold text-slate-900">Site Work Packages &amp; Milestone Progress</h2>
              <p className="text-xs text-slate-500">Field work packages (Civil, Ceiling, Tiles, Electrical, Painting)</p>
            </div>
            <button
              onClick={() => onNavigate('sitework')}
              className="text-xs font-semibold text-blue-700 hover:text-blue-900 bg-blue-50 px-3 py-1.5 rounded-lg"
            >
              Update Tasks →
            </button>
          </div>
          {Array.isArray(project.tasks) && project.tasks.length > 0 ? (
            <div className="space-y-3">
              {project.tasks.map((task: any) => (
                <div key={task.id} className="p-3 bg-slate-50 rounded-lg border border-slate-100 space-y-1.5">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-bold text-slate-800">{task.taskName}</span>
                    <span className="font-bold text-blue-700">{task.progressPercent}%</span>
                  </div>
                  <div className="w-full bg-slate-200 h-2 rounded-full overflow-hidden">
                    <div
                      className="bg-blue-600 h-full rounded-full transition-all"
                      style={{ width: `${task.progressPercent}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between text-[11px] text-slate-500">
                    <span>Category: {task.category} • Assigned: {task.assignedTo || 'Team'}</span>
                    <span className="font-semibold text-slate-700">{task.status}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-6 text-center text-xs text-slate-400">No site tasks scheduled yet.</div>
          )}
        </div>
      )}

      {/* Tab Content 8: Costing */}
      {activeTab === 'Cost' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-2xs p-5 space-y-4">
          <h2 className="text-sm font-bold text-slate-900">Project Cost Breakdown (Estimated vs Actual)</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
              <div className="text-[10px] text-slate-500 font-semibold uppercase">Contract Value</div>
              <div className="text-base font-bold text-slate-900 mt-1">{formatPKR(project.contractValue)}</div>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
              <div className="text-[10px] text-slate-500 font-semibold uppercase">Actual Incurred</div>
              <div className="text-base font-bold text-slate-900 mt-1">{formatPKR(project.actualCost)}</div>
            </div>
            <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-100">
              <div className="text-[10px] text-emerald-800 font-semibold uppercase">Gross Margin</div>
              <div className="text-base font-bold text-emerald-800 mt-1">{formatPKR(project.profit)}</div>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
              <div className="text-[10px] text-blue-800 font-semibold uppercase">Profit %</div>
              <div className="text-base font-bold text-blue-900 mt-1">
                {project.contractValue > 0
                  ? Math.round((Number(project.profit) / Number(project.contractValue)) * 100)
                  : 0}
                %
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab Content 9: Invoices */}
      {activeTab === 'Invoices' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-2xs p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold text-slate-900">Client Invoices</h2>
            <button
              onClick={() => onNavigate('invoices')}
              className="text-xs font-semibold text-blue-700 hover:text-blue-900 bg-blue-50 px-3 py-1.5 rounded-lg"
            >
              Open Invoices →
            </button>
          </div>
          {Array.isArray(project.invoices) && project.invoices.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {project.invoices.map((inv: any) => (
                <div key={inv.id} className="py-3 flex items-center justify-between text-xs">
                  <div>
                    <div className="font-bold text-blue-900">{inv.invoiceNumber}</div>
                    <div className="text-slate-500">Date: {inv.invoiceDate}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-slate-900">{formatPKR(inv.grandTotal)}</div>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
                      {inv.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-6 text-center text-xs text-slate-400">No invoices submitted yet for this project.</div>
          )}
        </div>
      )}

      {/* Tab Content 10: Payments */}
      {activeTab === 'Payments' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-2xs p-5 space-y-4">
          <h2 className="text-sm font-bold text-slate-900">Payments &amp; Banking Settlements</h2>
          <div className="p-6 text-center text-xs text-slate-400">
            Payment transactions are managed through Consolidated Invoicing &amp; Treasury Ledger.
          </div>
        </div>
      )}

      {/* Tab Content 11: Documents */}
      {activeTab === 'Documents' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-2xs p-5 space-y-4">
          <h2 className="text-sm font-bold text-slate-900">Project Drawings &amp; Verification Documents</h2>
          {Array.isArray(project.documents) && project.documents.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {project.documents.map((doc: any) => (
                <div key={doc.id} className="py-2.5 flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-blue-600" />
                    <div>
                      <div className="font-semibold text-slate-800">{doc.title}</div>
                      <div className="text-[11px] text-slate-400">
                        {doc.category} • {doc.fileSize}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShareData({
                          title: doc.title,
                          docType: 'DOCUMENT',
                          referenceNumber: project.projectNumber,
                          projectName: project.projectName,
                          detailsSummary: `${doc.category} file (${doc.fileSize})`,
                        });
                        setWhatsAppModalOpen(true);
                      }}
                      className="p-1 rounded-md bg-[#25D366]/15 hover:bg-[#25D366] text-[#075e54] hover:text-white transition-colors"
                      title="Share this file via WhatsApp"
                    >
                      <MessageSquare className="w-3.5 h-3.5" />
                    </button>
                    <span className="text-xs text-blue-700 font-semibold cursor-pointer hover:underline">
                      Download
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-6 text-center text-xs text-slate-400">No documents uploaded yet.</div>
          )}
        </div>
      )}

      {/* Tab Content 12: Activity */}
      {activeTab === 'Activity' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-2xs p-5 space-y-4">
          <h2 className="text-sm font-bold text-slate-900">Project Audit History</h2>
          <div className="p-4 bg-slate-50 rounded-lg text-xs text-slate-600">
            Project initialized on {project.startDate} by {project.projectManager}. All structural and financial changes are logged to the central audit trail.
          </div>
        </div>
      )}

      {/* WhatsApp Share Modal */}
      <WhatsAppShareModal
        isOpen={whatsAppModalOpen}
        onClose={() => setWhatsAppModalOpen(false)}
        data={shareData}
      />
    </div>
  );
};
